"use client";

import { CheckmarkNovaIcon } from "@/components/icons/CheckmarkNovaIcon";
import { LockedBlackIcon } from "@/components/icons/LockedBlackIcon";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { useLearning } from "@/context/NutrafolAcademyContext";
import type { NutrafolAcademyCourse } from "@/types/nutrafolAcademy";
import { cn } from "@/utils/general";

import CourseCardContent from "./CourseCardContent";

export interface CourseCardProps {
  index: number;
  course: NutrafolAcademyCourse;
}

const CourseCard = ({ index, course }: CourseCardProps) => {
  const { openStep, isLocked, isCompleted, skipToStep } = useLearning();

  const locked = isLocked(index);
  const completed = isCompleted(index);
  const prevCourse = index;

  let actionLabel = "Start Course";
  if (locked) {
    actionLabel = "Locked";
  } else if (completed) {
    actionLabel = "Watch Again";
  }

  let banner = null;
  if (completed) {
    banner = (
      <div className="flex gap-2 justify-center items-center p-2 bg-nova-brand-accent-80 rounded-t">
        <span className="w-5 h-5 lg:w-6 lg:h-6 text-nova-base-black">
          <CheckmarkNovaIcon width="100%" height="100%" />
        </span>
        <TextElementNova
          text={` Course ${index + 1} Completed`}
          classes={{
            default: "Body-10-Dia-Strong",
            1024: "Body-20-Dia-Strong",
          }}
        />
      </div>
    );
  } else if (locked) {
    banner = (
      <div className="flex gap-2 justify-center items-center p-2 bg-nova-base-white rounded-t">
        <span className="w-5 h-5 lg:w-6 lg:h-6 text-nova-base-black">
          <LockedBlackIcon width="100%" height="100%" />
        </span>
        <TextElementNova
          text={`Complete Course ${prevCourse} to Unlock`}
          classes={{
            default: "Body-10-Dia-Strong",
            1024: "Body-20-Dia-Strong",
          }}
        />
      </div>
    );
  }

  const footer = locked ? (
    <button
      type="button"
      className="flex mx-auto mt-3 px-1"
      aria-label={`I completed course ${prevCourse}`}
      onClick={(e) => skipToStep(e, index)}
    >
      <TextElementNova
        element="span"
        text={`I completed course ${prevCourse}`}
        className="CTA-10-Dia-Normal text-nova-base-white border-b border-b-nova-base-white"
      />
    </button>
  ) : null;

  return (
    <div>
      {banner}
      <div
        className={cn(
          "p-4 md:p-6 xl:p-10 rounded-sm bg-nova-transparency-white-10 border border-nova-transparency-white-10",
          banner && "rounded-t-none",
          !locked ? "cursor-pointer" : "cursor-default"
        )}
        onClick={() => !locked && openStep(index)}
      >
        <CourseCardContent
          course={course}
          tagLabel={`Course ${index + 1}`}
          actionLabel={actionLabel}
          locked={locked}
          footer={footer}
        />
      </div>
    </div>
  );
};

export default CourseCard;
